import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { CalendarClock, CalendarX, CheckCircle2, ChevronRight, ClipboardList, Clock, Eye, Layers, Lock } from 'lucide-react';
import { GET_AVAILABLE_SURVEYS, GET_MY_COMPLETED_SURVEYS, GET_MY_EXPIRED_SURVEYS } from '../../services/surveyQueries';
import { codiceErrore, getRepresentativeToken, getSurveyClient, percorsiArea } from '../../services/surveyAuth';
import { removeRepresentativeToken } from '../../services/representativeService';
import AssociativeAppShell from '../../components/AssociativeAppShell';
import { RepProfileCircolari, useProfiloRappresentante } from './AssociativeCircolari';

/**
 * Sondaggi nell'area associativa (rappresentanti con il permesso per carica
 * associativa.sondaggi.read): solo quelli riservati ai rappresentanti, a cui
 * il rappresentante risponde a titolo personale. Le query vanno sul client dei
 * sondaggi con il Bearer rappresentante; il filtro per pubblico è del server.
 */

export const PERMESSO_SONDAGGI = 'associativa.sondaggi.read';

const percorsi = percorsiArea('associativa');

const haPermesso = (profile: RepProfileCircolari | null, nome: string): boolean =>
  profile?.permissions?.some(p => p.name === nome) ?? false;

/** Shell dell'area associativa con la voce «Sondaggi» attiva. */
export const ShellSondaggi: React.FC<{ profile: RepProfileCircolari | null; children: React.ReactNode }> = ({ profile, children }) => (
  <AssociativeAppShell
    route="sondaggi"
    nome={profile?.nome}
    cognome={profile?.cognome}
    caricaPrincipale={profile?.caricaPrincipale}
    tipo={profile?.tipo}
    foto={profile?.foto}
    associatesAccess={profile?.associatesAccess}
    canLive={haPermesso(profile, 'associativa.live.publish')}
    canCircolari={haPermesso(profile, 'associativa.circolari.read')}
    canSondaggi={haPermesso(profile, PERMESSO_SONDAGGI)}
    canCalendario={haPermesso(profile, 'associativa.eventi.read')}
  >
    {children}
  </AssociativeAppShell>
);

type BloccoSondaggi = 'senza-permesso';

const TESTI_BLOCCO: Record<BloccoSondaggi, { titolo: string; testo: string }> = {
  'senza-permesso': {
    titolo: 'Le tue cariche non sono abilitate ai sondaggi',
    testo: 'Per l\'accesso ai sondaggi rivolgiti alla segreteria UNASCA.',
  },
};

/** Avviso in card: cariche non abilitate ai sondaggi. */
const AvvisoSondaggi: React.FC<{ blocco: BloccoSondaggi }> = ({ blocco }) => {
  const navigate = useNavigate();
  const { titolo, testo } = TESTI_BLOCCO[blocco];
  return (
    <section className="card">
      <div className="card-head">
        <ClipboardList size={16} />
        <h2>Sondaggi</h2>
      </div>
      <div className="card-body">
        <div role="alert" className="text-center px-4 py-10">
          <span className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center bg-gray-100 dark:bg-gray-700">
            <Lock className="w-5 h-5 text-gray-500 dark:text-gray-300" aria-hidden="true" />
          </span>
          <p className="text-base font-semibold text-gray-900 dark:text-white">{titolo}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 max-w-md mx-auto">{testo}</p>
          <button
            type="button"
            onClick={() => navigate(percorsi.home)}
            className="btn primary"
            style={{ marginTop: 20 }}
          >
            Torna alla dashboard
          </button>
        </div>
      </div>
    </section>
  );
};

interface SondaggioDisponibile {
  id: string;
  title: string;
  description: string;
  endDate: string | null;
  // Sondaggio territoriale con il titolo di un altro rappresentante: resta in elenco, non si apre
  avvisoTerritorio?: string | null;
  steps: { stepId: string; title: string }[];
}

interface SondaggioCompletato {
  surveyId: string;
  title: string;
  description: string | null;
  completedAt: string;
  stepCount: number;
}

// Sondaggio chiuso o scaduto senza una risposta inviata: solo da consultare
interface SondaggioScaduto {
  surveyId: string;
  title: string;
  description: string | null;
  closedAt: string;
  stepCount: number;
}

const formatoData = (iso: string) =>
  new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });

// Bearer rappresentante rifiutato: 401 del contesto GraphQL o codice UNAUTHENTICATED
const bearerRifiutato = (error: unknown): boolean =>
  codiceErrore(error) === 'UNAUTHENTICATED'
  || (error as { networkError?: { statusCode?: number } } | null)?.networkError?.statusCode === 401;

// Gli altri codici (compresi PUBBLICO_*, che il server filtra già) sono errori generici per card
const bloccoDaErrore = (error: unknown): BloccoSondaggi | null =>
  codiceErrore(error) === 'RAPPRESENTANTE_SENZA_PERMESSO' ? 'senza-permesso' : null;

const AssociativeSurveys: React.FC = () => {
  const navigate = useNavigate();
  const profile = useProfiloRappresentante();
  const abilitato = haPermesso(profile, PERMESSO_SONDAGGI);
  // Elenco: vale solo il Bearer rappresentante (il link firmato riguarda un singolo sondaggio)
  const headers = useMemo(() => {
    const token = getRepresentativeToken();
    return token ? { Authorization: `Bearer ${token}` } : undefined;
  }, []);
  const pronto = !!profile && abilitato && !!headers;

  // partitaIVA è String! nello schema: la stringa vuota lascia decidere il server (identità dal Bearer)
  const opzioni = {
    client: getSurveyClient(),
    context: { headers },
    variables: { partitaIVA: '' },
    fetchPolicy: 'network-only' as const,
    skip: !pronto,
  };
  const disponibili = useQuery(GET_AVAILABLE_SURVEYS, opzioni);
  const completati = useQuery(GET_MY_COMPLETED_SURVEYS, opzioni);
  const scaduti = useQuery(GET_MY_EXPIRED_SURVEYS, opzioni);

  const errori = [disponibili.error, completati.error, scaduti.error].filter(Boolean);
  const sessioneScaduta = (!!profile && !headers) || errori.some(bearerRifiutato);
  const blocco: BloccoSondaggi | null = profile && !abilitato
    ? 'senza-permesso'
    : errori.map(bloccoDaErrore).find(Boolean) || null;

  useEffect(() => {
    if (!sessioneScaduta) return;
    removeRepresentativeToken();
    navigate('/accesso-associati', { replace: true, state: { from: percorsi.elenco } });
  }, [sessioneScaduta, navigate]);

  const sondaggi: SondaggioDisponibile[] = disponibili.data?.availableSurveys || [];
  const fatti: SondaggioCompletato[] = completati.data?.myCompletedSurveys || [];
  const chiusi: SondaggioScaduto[] = scaduti.data?.myExpiredSurveys || [];
  const caricamento = !profile || disponibili.loading || completati.loading || scaduti.loading;
  // Errori generici calcolati per card: un errore sui soli completati non nasconde i disponibili.
  // Gli errori di accesso (blocco, sessione scaduta) restano globali.
  const accessoOk = !blocco && !sessioneScaduta;
  const erroreDisponibili = !!disponibili.error && accessoOk;
  const erroreCompletati = !!completati.error && accessoOk;
  const erroreScaduti = !!scaduti.error && accessoOk;

  let contenuto: React.ReactNode;
  if (blocco) {
    contenuto = <AvvisoSondaggi blocco={blocco} />;
  } else if (caricamento && sondaggi.length === 0 && fatti.length === 0 && chiusi.length === 0) {
    contenuto = (
      <div className="muted" style={{ textAlign: 'center', padding: '80px 0', fontSize: '13px' }}>Caricamento…</div>
    );
  } else {
    contenuto = (
      <>
        <section className="card">
          <div className="card-head">
            <ClipboardList size={16} />
            <h2>Disponibili</h2>
          </div>
          <div className="card-body">
            {erroreDisponibili ? (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400 text-center py-6">
                Impossibile caricare i sondaggi. Riprova più tardi.
              </p>
            ) : sondaggi.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                Nessun sondaggio riservato ai rappresentanti al momento
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {sondaggi.map(s => (
                  <li key={s.id} className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">{s.title}</h3>
                      {s.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{s.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Layers className="w-3.5 h-3.5" aria-hidden="true" />
                          {s.steps.length} step
                        </span>
                        {s.endDate && (
                          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                            <CalendarClock className="w-3.5 h-3.5" aria-hidden="true" />
                            Scade il {formatoData(s.endDate)}
                          </span>
                        )}
                      </div>
                      {s.avvisoTerritorio && (
                        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                          <Lock className="w-3.5 h-3.5 mt-px flex-shrink-0" aria-hidden="true" />
                          <span>{s.avvisoTerritorio}</span>
                        </p>
                      )}
                    </div>
                    {s.avvisoTerritorio ? (
                      <button type="button" className="btn ghost sm flex-shrink-0" disabled>
                        Non disponibile
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn primary sm flex-shrink-0"
                        onClick={() => navigate(percorsi.compila(s.id))}
                      >
                        Compila <ChevronRight size={14} aria-hidden="true" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {(fatti.length > 0 || erroreCompletati) && (
          <section className="card" style={{ marginTop: 20 }}>
            <div className="card-head">
              <CheckCircle2 size={16} />
              <h2>Completati</h2>
            </div>
            <div className="card-body">
              {erroreCompletati ? (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400 text-center py-6">
                  Impossibile caricare i sondaggi completati. Riprova più tardi.
                </p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {fatti.map(s => (
                    <li key={s.surveyId} className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">{s.title}</h3>
                        {s.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-1">{s.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-4 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Layers className="w-3.5 h-3.5" aria-hidden="true" />
                            {s.stepCount} step
                          </span>
                          <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                            Completato il {formatoData(s.completedAt)}
                          </span>
                        </div>
                      </div>
                      <Link to={percorsi.risposte(s.surveyId)} className="btn ghost sm flex-shrink-0 no-underline">
                        <Eye size={14} aria-hidden="true" /> Vedi le risposte
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}

        {/* Scaduti senza risposta: sola lettura, nessuna azione */}
        {(chiusi.length > 0 || erroreScaduti) && (
          <section className="card" style={{ marginTop: 20 }}>
            <div className="card-head">
              <CalendarX size={16} />
              <h2>Scaduti senza risposta</h2>
            </div>
            <div className="card-body">
              {erroreScaduti ? (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400 text-center py-6">
                  Impossibile caricare i sondaggi scaduti. Riprova più tardi.
                </p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {chiusi.map(s => (
                    <li
                      key={s.surveyId}
                      data-testid={`scaduto-${s.surveyId}`}
                      className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0 pl-3 border-l-4 border-gray-300 dark:border-gray-600"
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">{s.title}</h3>
                        {s.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-1">{s.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-4 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Layers className="w-3.5 h-3.5" aria-hidden="true" />
                            {s.stepCount} step
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                            Chiuso il {formatoData(s.closedAt)} · nessuna risposta inviata
                          </span>
                        </div>
                      </div>
                      <span className="flex-shrink-0 px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium rounded-full">
                        Chiuso
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}
      </>
    );
  }

  return (
    <ShellSondaggi profile={profile}>
      <div className="page fade-in">
        <header style={{ marginBottom: 18 }}>
          <h1 className="text-base font-semibold text-gray-900 dark:text-white">Sondaggi per i rappresentanti associativi</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Sondaggi riservati alla tua carica: le risposte sono personali
          </p>
        </header>
        {contenuto}
      </div>
    </ShellSondaggi>
  );
};

export default AssociativeSurveys;
